/**
 * Matrice compatibilità slot↔giocatore in modalità Mantra (Fantagazzetta).
 *
 * Categorie di compatibilità per uno (slot_ruolo, giocatore_ruolo):
 *   - exact:        nessun malus, schieramento regolare
 *   - malus:        schierabile fuori-ruolo con malus -1 al voto del giocatore
 *   - incompatible: non schierabile (es. movimento in porta, attaccante in difesa)
 *
 * Per slot composti (es. "Dc/B", "M/C", "W/A") la compatibilità è la
 * MIGLIORE tra le coppie possibili (slot_ruolo × giocatore_ruolo):
 * exact > malus > incompatible.
 *
 * Andrea (2026-05-18): il picker e il pitch usano questa matrice come
 * unica fonte di verità — niente più euristiche macro-categoria, sono
 * troppo permissive (es. macro vecchia faceva passare M/C su W/A come
 * exact, ma in Mantra W ed M sono ruoli specifici diversi).
 *
 * Fonte: tabella delle sostituzioni di Fantacalcio.it (Andrea 2026-09-24).
 * Exact = stesso ruolo o coppia in OK_FOR_SLOT; malus = coppia in
 * MALUS_FOR_SLOT (salvo NO_PER_MODULO); tutto il resto incompatible.
 */

import { RUOLI_MANTRA_ORDER } from "./ruoliMantra";

export type SlotCompat = "exact" | "malus" | "incompatible";

/** Tabella delle sostituzioni Mantra di Fantacalcio.it (Andrea 2026-09-24,
 * rifatta dalla tabella ufficiale: RIGA = ruolo dello slot da coprire,
 * COLONNA = ruolo di chi entra). Per ogni slot, i ruoli che lo coprono col
 * malus -1. Piu' lo slot e' arretrato, meno ruoli offensivi lo coprono: un
 * Pc puo' essere coperto da quasi tutti col -1, ma un Pc non copre nessuno
 * slot che non sia Pc o A. Le celle "OK negli schemi con i ruoli in
 * alternativa, negli altri casi NO" NON stanno qui: in uno slot composto
 * ("W/A") quel ruolo e' gia' esatto, in uno slot semplice e' vietato.
 * IDENTICA a `_MALUS_FOR_SLOT_MANTRA` nel backend (calcolo_service.py). */
export const MALUS_FOR_SLOT: Record<string, string[]> = {
  Por: [],
  Dc: ["B", "Dd", "Ds"],
  Dd: ["B", "Dc", "Ds"],
  Ds: ["B", "Dc", "Dd"],
  B: ["Dd", "Ds"],
  E: ["M", "B", "Dc", "Dd", "Ds"],
  M: ["E", "B", "Dc", "Dd", "Ds"],
  C: ["M", "E", "B", "Dc", "Dd", "Ds"],
  W: ["T", "C", "M", "E", "B", "Dc", "Dd", "Ds"],
  T: ["W", "C", "M", "E", "B", "Dc", "Dd", "Ds"],
  A: ["T", "W", "C", "M", "E", "B", "Dc", "Dd", "Ds"],
  Pc: ["T", "W", "C", "M", "E", "B", "Dc", "Dd", "Ds"],
};

/** Coppie OK SENZA malus anche a ruolo diverso (celle "OK" fuori dalla
 * diagonale): un A copre un Pc, un Dc copre un B. */
export const OK_FOR_SLOT: Record<string, string[]> = {
  Pc: ["A"],
  B: ["Dc"],
};

/** Eccezioni per modulo: coppie [slot, ruolo di chi entra] vietate in quel
 * modulo anche se la tabella le ammette col malus ("NO nel 4-1-4-1" per
 * W che copre T e T che copre W). */
export const NO_PER_MODULO: Record<string, Array<[string, string]>> = {
  "4-1-4-1": [
    ["T", "W"],
    ["W", "T"],
  ],
};

/** Parsing label slot mantra in ruoli accettati. Esempi:
 *    "Dc"     → ["Dc"]
 *    "Dc/B"   → ["Dc", "B"]
 *    "M/C"    → ["M", "C"]
 *    " "      → []
 */
export function parseSlotLabel(slotLabel: string | undefined): string[] {
  if (!slotLabel) return [];
  return slotLabel
    .split("/")
    .map((r) => r.trim())
    .filter((r) => r.length > 0 && RUOLI_MANTRA_ORDER.includes(r as never));
}

/** Compatibilità Mantra tra giocatore (lista ruoli) e slot (label).
 *
 * Itera tutte le coppie (slot_ruolo, giocatore_ruolo) e ritorna la migliore:
 * exact > malus > incompatible.
 */
export function slotCompatibilityMantra(
  ruoliGiocatore: readonly string[],
  slotLabel: string | undefined,
  /** Modulo schierato, per le eccezioni della tabella (W<->T nel 4-1-4-1). */
  modulo?: string,
): SlotCompat {
  const accettati = parseSlotLabel(slotLabel);
  if (accettati.length === 0) return "exact"; // slot generico
  if (!ruoliGiocatore || ruoliGiocatore.length === 0) return "incompatible";
  const vietate = NO_PER_MODULO[modulo ?? ""] ?? [];

  // Itera tutte le coppie; se trovo exact rientro subito, altrimenti
  // promuovo a "malus" se almeno una coppia matcha la tabella MALUS.
  let foundMalus = false;
  for (const slotR of accettati) {
    for (const playerR of ruoliGiocatore) {
      if (slotR === playerR) return "exact";
      if ((OK_FOR_SLOT[slotR] ?? []).includes(playerR)) return "exact";
      const malusList = MALUS_FOR_SLOT[slotR] ?? [];
      const vietata = vietate.some(([sl, pr]) => sl === slotR && pr === playerR);
      if (malusList.includes(playerR) && !vietata) foundMalus = true;
    }
  }
  return foundMalus ? "malus" : "incompatible";
}

/** Compatibilità Classic: il giocatore matcha se la prima lettera del
 * suo ruolo classic è in `accettati`. In Classic non esiste il malus
 * fuori-ruolo: o è compatibile (exact) o non lo è (incompatible). */
export function slotCompatibilityClassic(
  ruoloClassic: string | null,
  slotLabel: string | undefined,
): SlotCompat {
  if (!slotLabel) return "exact";
  const accettati = slotLabel
    .split("/")
    .map((r) => r.trim())
    .filter(Boolean);
  if (accettati.length === 0) return "exact";
  if (!ruoloClassic) return "incompatible";
  const macro = ruoloClassic.charAt(0).toUpperCase();
  return accettati.includes(macro) ? "exact" : "incompatible";
}

/** Entry-point unificato (Mantra o Classic) per UI: prende l'oggetto
 * giocatore e ritorna la compatibilità con lo slot. */
export function slotCompatibility(
  player: { ruoli_mantra: string[] | null; ruolo_classic: string | null },
  slotLabel: string | undefined,
  isMantra: boolean,
  modulo?: string,
): SlotCompat {
  return isMantra
    ? slotCompatibilityMantra(player.ruoli_mantra ?? [], slotLabel, modulo)
    : slotCompatibilityClassic(player.ruolo_classic, slotLabel);
}

/** Label UI per ogni stato di compatibilità (tag + classe Tailwind). */
export const COMPAT_LABEL: Record<SlotCompat, { tag: string; class: string }> = {
  exact: { tag: "✓ Compatibile", class: "text-success" },
  malus: { tag: "⚠ Malus -1 (fuori ruolo)", class: "text-warning" },
  incompatible: { tag: "✗ Non schierabile", class: "text-danger" },
};
